import { Injectable } from '@angular/core';
import { readSupportSession } from '@modules/auth/models/support-session.model';
import { UserService } from '@modules/auth/services/user.service';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Acceso a «Mejora tu Fina» (`/comercio/mejora-tu-fina`, su pestaña en Preferencias del comercio, el enlace del
 * banner del POS y la compra de SMS de la pestaña Planes) = dueño del comercio (decisión de negocio, 2026-11-03:
 * la página y sus compras son solo del dueño; un usuario asociado no ve la pestaña y el api le responde 403).
 * Una sesión de soporte de Fina entra en modo lectura: ve saldos y paquetes, pero no compra ni solicita.
 *
 * Vive en `app-common` porque lo consumen `commerce`, `navigation` y `fina-sms-messages`. Patrón:
 * `FiscalOnboardingFlagService` (dueño o soporte) sin flag de PostHog.
 */
@Injectable({ providedIn: 'root' })
export class ImproveFinaAccessService {
    constructor(private readonly userService: UserService) {}

    /** Ver la página y sus entradas: dueño o sesión de soporte. Para `*ngIf`, pestañas y el guard. */
    canAccessSync(): boolean {
        return this.isOwner() || this.isSupportSession();
    }

    /** Comprar o solicitar: solo el dueño (el api responde 403 a soporte y asociados). */
    canOperateSync(): boolean {
        return this.isOwner() && !this.isSupportSession();
    }

    /** Emite el acceso cuando cambia la sesión; arranca con el valor actual (`user$` es un BehaviorSubject). */
    canAccessChanges$(): Observable<boolean> {
        return this.userService.user$.pipe(
            map(() => this.canAccessSync()),
            distinctUntilChanged(),
        );
    }

    private isOwner(): boolean {
        return !!this.userService.user$.value?.isOwner;
    }

    /** Sesión de soporte de Fina (impersonación). Método aparte para poder simularlo en los specs. */
    protected isSupportSession(): boolean {
        return !!readSupportSession();
    }
}
